/**
 * Главное меню приложения.
 *
 * Ничего не знает о разделах: берёт их из `nav/sections`. Добавленный
 * туда раздел появляется здесь сам — в этом и был смысл списка.
 *
 * Слева то, куда ходят работать, справа — то, что относится к самому
 * человеку. Разделение держится не порядком в разметке, а полем `side`
 * у раздела: иначе пятый раздел встал бы туда, где оказался в списке,
 * а не туда, где ему место.
 */

import { BrandLogo } from '../brand/BrandLogo.js';
import type { JSX } from 'react';
import { sectionsOn, type Section } from '../nav/sections.js';
import { useSession } from '../state/session.js';

interface Props {
  current: string;
  onGo(id: string): void;
  /**
   * Сколько уведомлений человек ещё не видел.
   *
   * Ноль значков не рисует. Значок над колокольчиком без числа —
   * обещание, которого может не быть за ним ничего: пусть он появляется
   * только когда есть что показать.
   */
  unread: number;
}

export function AppNav({ current, onGo, unread }: Props): JSX.Element {
  const session = useSession();

  return (
    <nav className="nav" aria-label="Разделы">
      <span className="nav-brand">
        <BrandLogo />
      </span>

      <span className="nav-sep" aria-hidden="true" />

      {/*
        Имена групп явные, а не «первая» и «вторая».
        
        На телефоне разделы уезжают в нижнюю панель, а колокольчик с
        лицом остаются наверху — и правило, отличающее их по порядку в
        разметке, сломалось бы от первой же перестановки.
      */}
      <div className="nav-group nav-group--main">
        {sectionsOn('main').map((section) => (
          <NavItem
            key={section.id}
            section={section}
            active={section.id === current}
            onGo={onGo}
          />
        ))}
      </div>

      <span className="nav-spacer" />

      <div className="nav-group nav-group--user">
        {sectionsOn('user').map((section, index) => (
          <span key={section.id} className="nav-cell">
            {/* Разделитель между правыми разделами, но не перед первым:
                слева от него уже стоит своя черта. */}
            {index > 0 && <span className="nav-sep" aria-hidden="true" />}
            <NavItem
              section={section}
              active={section.id === current}
              onGo={onGo}
              badge={section.id === 'notifications' ? unread : 0}
              plain
              /*
               * Подписи справа не нужны: колокольчик узнаётся сам, а на
               * месте «Личного кабинета» стоит лицо человека — оно
               * говорит о владельце больше, чем два слова. Для тех, кто
               * слушает экран, подпись остаётся в разметке.
               */
              hideLabel={section.id === 'notifications' || section.id === 'account'}
              {...(section.id === 'account'
                ? { avatar: session?.avatar ?? null, initial: initialOf(session?.name) }
                : {})}
            />
          </span>
        ))}
      </div>
    </nav>
  );
}

/** Первая буква имени для монограммы. */
function initialOf(name: string | undefined): string {
  return (name ?? '?').trim().slice(0, 1).toUpperCase() || '?';
}

function NavItem({
  section,
  active,
  onGo,
  badge = 0,
  plain = false,
  hideLabel = false,
  avatar,
  initial,
}: {
  section: Section;
  active: boolean;
  onGo(id: string): void;
  badge?: number;
  /** Без обводки-пилюли: правая группа рисуется спокойнее левой. */
  plain?: boolean;
  /** Значка достаточно: подпись только занимает место. */
  hideLabel?: boolean;
  /**
   * Аватарка вместо значка. Пусто — рисуется монограмма.
   *
   * Различие не косметическое: фотография есть не у всех, и пустой
   * кружок на её месте выглядел бы поломкой. Монограмма — обычное
   * состояние, а не запасное.
   */
  avatar?: string | null;
  initial?: string;
}): JSX.Element {
  const Icon = section.icon;
  const face = avatar !== undefined || initial !== undefined;
  const classes = ['nav-item'];
  if (plain) classes.push('nav-item--plain');
  if (active) classes.push('nav-item--on');

  return (
    <button
      type="button"
      className={classes.join(' ')}
      data-section={section.id}
      aria-current={active ? 'page' : undefined}
      onClick={() => onGo(section.id)}
    >
      <span className="nav-icon">
        {face ? (
          avatar ? (
            /*
             * Подпись у картинки пустая: имя человека стоит рядом в
             * названии кнопки, и читать его дважды подряд экранному
             * диктору незачем.
             */
            <img className="nav-avatar" src={avatar} alt="" />
          ) : (
            <span className="nav-avatar nav-avatar--letter" aria-hidden="true">
              {initial}
            </span>
          )
        ) : (
          <Icon />
        )}
        {badge > 0 && (
          <span className="nav-badge" aria-label={`непрочитанных: ${badge}`}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      {hideLabel ? (
        // Подписи нет на экране, но она остаётся для тех, кто слушает.
        <span className="nav-hidden">{section.label}</span>
      ) : (
        <span className="nav-label">{section.label}</span>
      )}
    </button>
  );
}
